/**
 * Bookmarks service layer.
 * Manages bookmark (saved post) documents stored under users/{userId}/bookmarks/{postId}.
 */
import {
  doc,
  setDoc,
  deleteDoc,
  getDoc,
  getDocs,
  query,
  collection,
  orderBy,
  limit as firestoreLimit,
  startAfter,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from './firebase';
import { handleServiceError } from '../utils/error-handler.util';

const USERS_COLLECTION = 'users';
const BOOKMARKS_SUBCOLLECTION = 'bookmarks';

/**
 * Get the bookmarks subcollection reference for a user.
 */
const getBookmarksRef = (userId) =>
  collection(db, USERS_COLLECTION, userId, BOOKMARKS_SUBCOLLECTION);

/**
 * Bookmark a post.
 * @param {string} userId
 * @param {string} postId
 * @param {string} postType - 'text' | 'workout' | 'meal' | 'progress'
 */
export const bookmarkPost = async (userId, postId, postType = 'text') => {
  try {
    const docRef = doc(db, USERS_COLLECTION, userId, BOOKMARKS_SUBCOLLECTION, postId);
    await setDoc(docRef, {
      postId,
      postType,
      createdAt: serverTimestamp(),
    });
  } catch (error) {
    handleServiceError(error, 'Bookmark post', 'Failed to bookmark post. Please try again.');
  }
};

/**
 * Remove a bookmark.
 * @param {string} userId
 * @param {string} postId
 */
export const unbookmarkPost = async (userId, postId) => {
  try {
    const docRef = doc(db, USERS_COLLECTION, userId, BOOKMARKS_SUBCOLLECTION, postId);
    await deleteDoc(docRef);
  } catch (error) {
    handleServiceError(error, 'Unbookmark post', 'Failed to remove bookmark. Please try again.');
  }
};

/**
 * Get paginated bookmarks for a user.
 * @param {string} userId
 * @param {*} lastDoc - Firestore document snapshot for pagination
 * @param {number} pageLimit
 * @returns {Promise<{bookmarks: Array, lastDoc: *, hasMore: boolean}>}
 */
export const getBookmarks = async (userId, lastDoc = null, pageLimit = 20) => {
  try {
    const ref = getBookmarksRef(userId);
    let q = query(ref, orderBy('createdAt', 'desc'), firestoreLimit(pageLimit));

    if (lastDoc) {
      q = query(ref, orderBy('createdAt', 'desc'), startAfter(lastDoc), firestoreLimit(pageLimit));
    }

    const snapshot = await getDocs(q);
    const bookmarks = snapshot.docs.map((docSnap) => ({
      id: docSnap.id,
      ...docSnap.data(),
    }));

    const newLastDoc = snapshot.docs.length > 0 ? snapshot.docs[snapshot.docs.length - 1] : null;
    const hasMore = snapshot.docs.length === pageLimit;

    return { bookmarks, lastDoc: newLastDoc, hasMore };
  } catch (error) {
    handleServiceError(error, 'Get bookmarks', 'Failed to load bookmarks. Please try again.');
  }
};

/**
 * Check if a single post is bookmarked.
 * @param {string} userId
 * @param {string} postId
 * @returns {Promise<boolean>}
 */
export const checkIfBookmarked = async (userId, postId) => {
  try {
    const docRef = doc(db, USERS_COLLECTION, userId, BOOKMARKS_SUBCOLLECTION, postId);
    const docSnap = await getDoc(docRef);
    return docSnap.exists();
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('[checkIfBookmarked]', error);
    }
    return false;
  }
};

/**
 * Check bookmark status for multiple posts at once.
 * @param {string} userId
 * @param {string[]} postIds
 * @returns {Promise<Set<string>>} Set of bookmarked post IDs
 */
export const checkBookmarkedBatch = async (userId, postIds) => {
  try {
    if (!postIds || postIds.length === 0) return new Set();

    const bookmarkedSet = new Set();
    // Check each post individually (Firestore doesn't support IN on subcollection doc IDs)
    await Promise.all(
      postIds.map(async (postId) => {
        const isBookmarked = await checkIfBookmarked(userId, postId);
        if (isBookmarked) bookmarkedSet.add(postId);
      })
    );

    return bookmarkedSet;
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('[checkBookmarkedBatch]', error);
    }
    return new Set();
  }
};
